<template>
  <div class="space-y-4">
    <div>
      <label class="block text-sm font-medium mb-2">
        {{ t('configuracoes.tagIconLabel') }}
      </label>
      <div class="icon-grid-container">
        <div class="grid grid-cols-8 gap-2">
        <button
          v-for="icon in availableIcons"
          :key="icon.value"
          @click="selectIcon(icon.value)"
          type="button"
          class="w-12 h-12 rounded-lg border-2 transition-all flex items-center justify-center hover:scale-105"
          :class="selectedIcon === icon.value 
            ? 'border-blue-500 bg-blue-500 bg-opacity-20 shadow-lg' 
            : 'border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50'"
          :disabled="disabled"
          :title="icon.label"
        >
          <span class="text-2xl">{{ icon.emoji }}</span>
        </button>
        </div>
      </div>
      
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  modelValue: {
    type: String,
    default: 'task'
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const selectedIcon = ref(props.modelValue)

// Ícones disponíveis para tags - Coleção otimizada
const iconDefs = [
  // Prioridade e Status
  { value: 'priority', emoji: '🔥' },
  { value: 'urgent', emoji: '⚡' },
  { value: 'important', emoji: '⭐' },
  { value: 'critical', emoji: '🚨' },
  { value: 'high', emoji: '🔴' },
  { value: 'medium', emoji: '🟡' },
  { value: 'low', emoji: '🟢' },
  { value: 'done', emoji: '✅' },
  { value: 'pending', emoji: '⏳' },
  { value: 'blocked', emoji: '🚫' },
  { value: 'progress', emoji: '🔄' },
  { value: 'review', emoji: '👀' },
  { value: 'approval', emoji: '👍' },
  { value: 'rejection', emoji: '👎' },
  { value: 'warning', emoji: '⚠️' },
  { value: 'error', emoji: '❌' },
  { value: 'success', emoji: '🎉' },
  { value: 'info', emoji: 'ℹ️' },
  { value: 'question', emoji: '❓' },
  { value: 'help', emoji: '🆘' },
  
  // Trabalho e Produtividade
  { value: 'task', emoji: '📋' },
  { value: 'project', emoji: '📁' },
  { value: 'bug', emoji: '🐛' },
  { value: 'feature', emoji: '✨' },
  { value: 'improvement', emoji: '🔧' },
  { value: 'test', emoji: '🧪' },
  { value: 'deploy', emoji: '🚀' },
  { value: 'release', emoji: '🎊' },
  { value: 'maintenance', emoji: '🔨' },
  { value: 'security', emoji: '🔒' },
  { value: 'database', emoji: '🗄️' },
  { value: 'api', emoji: '🔌' },
  { value: 'frontend', emoji: '🎨' },
  { value: 'backend', emoji: '⚙️' },
  { value: 'mobile', emoji: '📱' },
  { value: 'web', emoji: '🌐' },
  { value: 'desktop', emoji: '💻' },
  
  // Comunicação e Colaboração
  { value: 'meeting', emoji: '📅' },
  { value: 'call', emoji: '📞' },
  { value: 'email', emoji: '📧' },
  { value: 'chat', emoji: '💬' },
  { value: 'team', emoji: '👥' },
  { value: 'client', emoji: '👤' },
  { value: 'admin', emoji: '👑' },
  { value: 'manager', emoji: '👔' },
  { value: 'developer', emoji: '👨‍💻' },
  { value: 'designer', emoji: '🎨' },
  { value: 'analyst', emoji: '📊' },
  { value: 'consultant', emoji: '💼' },
  { value: 'support', emoji: '🆘' },
  { value: 'training', emoji: '🎓' },
  { value: 'documentation', emoji: '📚' },
  { value: 'tutorial', emoji: '📖' },
  { value: 'guide', emoji: '🗺️' },
  { value: 'manual', emoji: '📘' },
  
  // Tempo e Organização
  { value: 'clock', emoji: '⏰' },
  { value: 'calendar', emoji: '📆' },
  { value: 'deadline', emoji: '⏳' },
  { value: 'reminder', emoji: '🔔' },
  { value: 'event', emoji: '🎯' },
  { value: 'milestone', emoji: '🏁' },
  { value: 'checkpoint', emoji: '📍' },
  { value: 'phase', emoji: '📈' },
  { value: 'sprint', emoji: '🏃‍♂️' },
  { value: 'quarter', emoji: '📅' },
  { value: 'monthly', emoji: '📅' },
  { value: 'weekly', emoji: '📅' },
  { value: 'daily', emoji: '📅' },
  
  // Finanças e Negócios
  { value: 'money', emoji: '💰' },
  { value: 'budget', emoji: '💳' },
  { value: 'cost', emoji: '💸' },
  { value: 'revenue', emoji: '💵' },
  { value: 'profit', emoji: '📈' },
  { value: 'investment', emoji: '📊' },
  { value: 'sales', emoji: '🛒' },
  { value: 'marketing', emoji: '📢' },
  { value: 'promotion', emoji: '🎯' },
  { value: 'campaign', emoji: '📢' },
  { value: 'lead', emoji: '🎣' },
  { value: 'prospect', emoji: '🔍' },
  { value: 'contract', emoji: '📄' },
  { value: 'invoice', emoji: '🧾' },
  { value: 'payment', emoji: '💳' },
  { value: 'refund', emoji: '↩️' },
  { value: 'discount', emoji: '🏷️' },
  { value: 'offer', emoji: '🎁' },
  { value: 'deal', emoji: '🤝' },
  
  // Mídia e Conteúdo
  { value: 'website', emoji: '🌐' },
  { value: 'social', emoji: '📱' },
  { value: 'blog', emoji: '📝' },
  { value: 'article', emoji: '📰' },
  { value: 'news', emoji: '📰' },
  { value: 'video', emoji: '🎥' },
  { value: 'image', emoji: '🖼️' },
  { value: 'audio', emoji: '🎵' },
  { value: 'podcast', emoji: '🎙️' },
  { value: 'livestream', emoji: '📺' },
  { value: 'webinar', emoji: '🎓' },
  { value: 'course', emoji: '🎓' },
  { value: 'ebook', emoji: '📖' },
  { value: 'whitepaper', emoji: '📄' },
  { value: 'case-study', emoji: '📊' },
  { value: 'infographic', emoji: '📊' },
  { value: 'brochure', emoji: '📋' },
  { value: 'catalog', emoji: '📚' },
  
  // Localização e Logística
  { value: 'location', emoji: '📍' },
  { value: 'office', emoji: '🏢' },
  { value: 'home', emoji: '🏠' },
  { value: 'remote', emoji: '🏡' },
  { value: 'travel', emoji: '✈️' },
  { value: 'delivery', emoji: '🚚' },
  { value: 'shipping', emoji: '📦' },
  { value: 'warehouse', emoji: '🏭' },
  { value: 'store', emoji: '🏪' },
  { value: 'factory', emoji: '🏭' },
  { value: 'supplier', emoji: '🏭' },
  { value: 'vendor', emoji: '🏪' },
  { value: 'partner', emoji: '🤝' },
  { value: 'distributor', emoji: '🚛' },
  { value: 'retailer', emoji: '🏪' },
  { value: 'wholesale', emoji: '📦' },
  { value: 'import', emoji: '📥' },
  { value: 'export', emoji: '📤' },
  { value: 'customs', emoji: '🛂' },
  { value: 'logistics', emoji: '🚛' },
  
  // Tecnologia e Inovação
  { value: 'innovation', emoji: '💡' },
  { value: 'research', emoji: '🔬' },
  { value: 'development', emoji: '🔧' },
  { value: 'prototype', emoji: '🧪' },
  { value: 'pilot', emoji: '✈️' },
  { value: 'beta', emoji: '🧪' },
  { value: 'ai', emoji: '🤖' },
  { value: 'ml', emoji: '🧠' },
  { value: 'blockchain', emoji: '⛓️' },
  { value: 'crypto', emoji: '₿' },
  { value: 'cloud', emoji: '☁️' },
  { value: 'iot', emoji: '🌐' },
  { value: 'ar', emoji: '🥽' },
  { value: 'vr', emoji: '🥽' },
  { value: 'automation', emoji: '🤖' },
  { value: 'integration', emoji: '🔗' },
  { value: 'migration', emoji: '🔄' },
  { value: 'upgrade', emoji: '⬆️' },
  { value: 'downgrade', emoji: '⬇️' },
  { value: 'rollback', emoji: '↩️' },
  
  // ===== ORIGENS DE LEAD - CRM =====
  // Redes Sociais e Plataformas Digitais
  { value: 'facebook', emoji: '📘' },
  { value: 'instagram', emoji: '📷' },
  { value: 'twitter', emoji: '🐦' },
  { value: 'linkedin', emoji: '💼' },
  { value: 'youtube', emoji: '📺' },
  { value: 'tiktok', emoji: '🎵' },
  { value: 'whatsapp', emoji: '💬' },
  { value: 'telegram', emoji: '✈️' },
  { value: 'discord', emoji: '🎮' },
  { value: 'snapchat', emoji: '👻' },
  { value: 'pinterest', emoji: '📌' },
  { value: 'reddit', emoji: '🤖' },
  { value: 'twitch', emoji: '🎮' },
  { value: 'clubhouse', emoji: '🏠' },
  
  // Motores de Busca e Diretórios
  { value: 'google', emoji: '🔍' },
  { value: 'bing', emoji: '🔍' },
  { value: 'yahoo', emoji: '🔍' },
  { value: 'duckduckgo', emoji: '🦆' },
  { value: 'directory', emoji: '📋' },
  { value: 'yellow-pages', emoji: '📞' },
  { value: 'business-directory', emoji: '🏢' },
  
  // Websites e Plataformas Online
  { value: 'website', emoji: '🌐' },
  { value: 'landing-page', emoji: '🎯' },
  { value: 'blog', emoji: '📝' },
  { value: 'ecommerce', emoji: '🛒' },
  { value: 'marketplace', emoji: '🏪' },
  { value: 'amazon', emoji: '📦' },
  { value: 'mercadolivre', emoji: '🛒' },
  { value: 'shopee', emoji: '🛍️' },
  { value: 'magalu', emoji: '🛒' },
  { value: 'americanas', emoji: '🛒' },
  
  // Email e Marketing Digital
  { value: 'email-marketing', emoji: '📧' },
  { value: 'newsletter', emoji: '📰' },
  { value: 'newsletter-signup', emoji: '📝' },
  { value: 'lead-magnet', emoji: '🧲' },
  { value: 'webinar', emoji: '🎓' },
  { value: 'live', emoji: '📺' },
  { value: 'podcast', emoji: '🎙️' },
  { value: 'youtube-channel', emoji: '📺' },
  
  // Publicidade e Anúncios
  { value: 'google-ads', emoji: '🎯' },
  { value: 'facebook-ads', emoji: '📘' },
  { value: 'instagram-ads', emoji: '📷' },
  { value: 'linkedin-ads', emoji: '💼' },
  { value: 'youtube-ads', emoji: '📺' },
  { value: 'tiktok-ads', emoji: '🎵' },
  { value: 'display-ads', emoji: '🖼️' },
  { value: 'retargeting', emoji: '🔄' },
  { value: 'affiliate', emoji: '🤝' },
  { value: 'influencer', emoji: '⭐' },
  
  // Eventos e Networking
  { value: 'event', emoji: '🎪' },
  { value: 'conference', emoji: '🎤' },
  { value: 'workshop', emoji: '🔧' },
  { value: 'seminar', emoji: '🎓' },
  { value: 'networking', emoji: '🤝' },
  { value: 'trade-show', emoji: '🏢' },
  { value: 'exhibition', emoji: '🎨' },
  { value: 'meetup', emoji: '👥' },
  { value: 'business-lunch', emoji: '🍽️' },
  
  // Indicações e Referências
  { value: 'referral', emoji: '👥' },
  { value: 'word-of-mouth', emoji: '🗣️' },
  { value: 'recommendation', emoji: '👍' },
  { value: 'partner', emoji: '🤝' },
  { value: 'client-referral', emoji: '👤' },
  { value: 'supplier-referral', emoji: '🏭' },
  { value: 'employee-referral', emoji: '👨‍💼' },
  
  // Canais Tradicionais
  { value: 'phone', emoji: '📞' },
  { value: 'walk-in', emoji: '🚶' },
  { value: 'store-visit', emoji: '🏪' },
  { value: 'office-visit', emoji: '🏢' },
  { value: 'mail', emoji: '📮' },
  { value: 'fax', emoji: '📠' },
  { value: 'radio', emoji: '📻' },
  { value: 'tv', emoji: '📺' },
  { value: 'newspaper', emoji: '📰' },
  { value: 'magazine', emoji: '📖' },
  { value: 'billboard', emoji: '🪧' },
  { value: 'flyer', emoji: '📄' },
  { value: 'brochure', emoji: '📋' },
  
  // Outros Canais Digitais
  { value: 'app-store', emoji: '📱' },
  { value: 'play-store', emoji: '📱' },
  { value: 'qr-code', emoji: '📱' },
  { value: 'nfc', emoji: '📱' },
  { value: 'beacon', emoji: '📡' },
  { value: 'chatbot', emoji: '🤖' },
  { value: 'live-chat', emoji: '💬' },
  { value: 'support-ticket', emoji: '🎫' },
  
  // Canais Específicos por Setor
  { value: 'real-estate', emoji: '🏠' },
  { value: 'automotive', emoji: '🚗' },
  { value: 'healthcare', emoji: '🏥' },
  { value: 'education', emoji: '🎓' },
  { value: 'finance', emoji: '💰' },
  { value: 'insurance', emoji: '🛡️' },
  { value: 'legal', emoji: '⚖️' },
  { value: 'consulting', emoji: '💼' },
  { value: 'technology', emoji: '💻' },
  { value: 'retail', emoji: '🛍️' },
  
  // Outros
  { value: 'other', emoji: '❓' },
  { value: 'unknown', emoji: '❔' },
  { value: 'competitor', emoji: '⚔️' },
  { value: 'research', emoji: '🔍' },
  { value: 'cold-call', emoji: '❄️' },
  { value: 'cold-email', emoji: '📧' }
]

const availableIcons = computed(() =>
  iconDefs.map((icon) => ({
    ...icon,
    label: t(`tagIcons.${icon.value}`)
  }))
)

const selectIcon = (iconValue) => {
  if (props.disabled) return
  
  selectedIcon.value = iconValue
  emit('update:modelValue', iconValue)
}

const getSelectedIconEmoji = () => {
  const icon = availableIcons.find(i => i.value === selectedIcon.value)
  return icon ? icon.emoji : '🏷️'
}

const getSelectedIconLabel = () => {
  const icon = availableIcons.find(i => i.value === selectedIcon.value)
  return icon ? icon.label : t('tagIcons.defaultLabel')
}

// Sincronizar com prop externa
watch(() => props.modelValue, (newValue) => {
  selectedIcon.value = newValue
}, { immediate: true })
</script>

<style scoped>
/* Container com scroll para os ícones */
.icon-grid-container {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px;
  background-color: #f9fafb;
}

.icon-grid-container::-webkit-scrollbar {
  width: 6px;
}

.icon-grid-container::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.icon-grid-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.icon-grid-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Estilos para tema escuro */
.tema_escura .border-gray-300 {
  border-color: var(--border-secondary);
}

.tema_escura .border-gray-400 {
  border-color: var(--border-primary);
}

.tema_escura .bg-white {
  background-color: var(--bg-secondary);
}

.tema_escura .bg-gray-50 {
  background-color: var(--bg-tertiary);
}

.tema_escura .bg-gray-100 {
  background-color: var(--bg-tertiary);
}

.tema_escura .text-gray-600 {
  color: var(--text-secondary);
}

.tema_escura .text-gray-400 {
  color: var(--text-tertiary);
}

.tema_escura .icon-grid-container {
  border-color: var(--border-secondary);
  background-color: var(--bg-tertiary);
}

.tema_escura .icon-grid-container::-webkit-scrollbar-track {
  background: var(--bg-primary);
}

.tema_escura .icon-grid-container::-webkit-scrollbar-thumb {
  background: var(--border-primary);
}
</style>
